import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Filter,
  FileText,
  ChevronDown,
  Plus,
  Phone,
  MessageCircle,
  Truck,
  Box,
  CheckCircle,
  Check,
  MoreHorizontal,
  Loader,
  AlertCircle,
  X,
  Calendar,
  Tag,
  Layout,
  CreditCard,
  User,
} from "lucide-react";
import ThemeSelect from "../components/ThemeSelect";
import ExpandableSearchBar from "../components/ExpandableSearchBar";
import { useTheme } from "../context/ThemeContext";
import { ordersApi, employeesApi, productsApi, orderCampaignApi } from "../services/api";
import { Dropdown, Checkbox, message, DatePicker } from "antd";
import dayjs from "dayjs";
import { usePermissions } from "../hooks/Usepermissions";
import Pagination from "../components/Pagination";
import ExcelExportButton from "../components/ExcelExportButton";
import { BASE_URL as API_URL } from '../services/api';

const { RangePicker } = DatePicker;

// ─── Bulk Status API helper ────────────────────────────────────────────────────
const bulkUpdateStatus = async (orderIds, status, remarks = "") => {
  const response = await fetch(
    `${API_URL}/workflow/bulk/status`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
      },
      body: JSON.stringify({ orderIds, status, remarks }),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      err.message || err.error || `API Error: ${response.status}`,
    );
  }
  return response.json();
};

// ─── Date preset → explicit startDate/endDate (YYYY-MM-DD) ────────────────────
const pad = (n) => String(n).padStart(2, "0");
const toDateStr = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const computeDateRange = (key, customStart, customEnd) => {
  if (!key || key === "all_time") return null;
  const today = new Date();

  switch (key) {
    case "today":
      return { startDate: toDateStr(today), endDate: toDateStr(today) };
    case "yesterday": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { startDate: toDateStr(y), endDate: toDateStr(y) };
    }
    case "this_week": {
      const day = today.getDay() || 7; // Mon=1..Sun=7
      const monday = new Date(today);
      monday.setDate(today.getDate() - day + 1);
      return { startDate: toDateStr(monday), endDate: toDateStr(today) };
    }
    case "last_week": {
      const day = today.getDay() || 7;
      const thisMonday = new Date(today);
      thisMonday.setDate(today.getDate() - day + 1);
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(thisMonday.getDate() - 7);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 6);
      return {
        startDate: toDateStr(lastMonday),
        endDate: toDateStr(lastSunday),
      };
    }
    case "this_month": {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: toDateStr(first), endDate: toDateStr(today) };
    }
    case "last_month": {
      const firstLast = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastLast = new Date(today.getFullYear(), today.getMonth(), 0);
      return { startDate: toDateStr(firstLast), endDate: toDateStr(lastLast) };
    }
    case "this_year": {
      const first = new Date(today.getFullYear(), 0, 1);
      return { startDate: toDateStr(first), endDate: toDateStr(today) };
    }
    case "custom":
      if (customStart && customEnd)
        return { startDate: customStart, endDate: customEnd };
      return null;
    default:
      return null;
  }
};

// ─── Excel Bulk Status Modal ───────────────────────────────────────────────────
const ExcelBulkModal = ({ isOpen, onClose, currentTheme, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState("upload");
  const [result, setResult] = useState(null);
  const fileInputRef = React.useRef(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    setActiveStep("upload");
    onClose();
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      alert("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }
    setSelectedFile(file);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDownloadTemplate = async () => {
    setIsLoading(true);
    try {
      const blob = await ordersApi.downloadBulkStatusTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "bulk-status-update-template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      message.success("Template downloaded!");
    } catch (err) {
      message.error(err.message || "Failed to download template");
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    try {
      const data = await ordersApi.validateBulkStatusExcel(selectedFile);
      setResult({ type: "validate", data });
      setActiveStep("result");
    } catch (err) {
      setResult({ type: "error", data: { message: err.message } });
      setActiveStep("result");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    try {
      const data = await ordersApi.uploadBulkStatusExcel(selectedFile);
      setResult({ type: "upload", data });
      setActiveStep("result");
      onSuccess && onSuccess(data);
    } catch (err) {
      setResult({ type: "error", data: { message: err.message } });
      setActiveStep("result");
    } finally {
      setIsLoading(false);
    }
  };

  const renderResult = () => {
    if (!result) return null;

    if (result.type === "error") {
      return (
        <div className="flex flex-col items-center py-6">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h4 className="font-bold text-red-700 text-base mb-1">Failed</h4>
          <p className="text-sm text-gray-500 text-center max-w-xs">
            {result.data.message}
          </p>
          <button
            onClick={() => {
              setResult(null);
              setActiveStep("upload");
            }}
            className="mt-4 px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            Try Again
          </button>
        </div>
      );
    }

    const d = result.data;
    const isValidate = result.type === "validate";

    const total = d.totalProcessed ?? d.totalRows ?? d.total ?? "-";
    const success = d.successCount ?? d.success ?? d.valid ?? "-";
    const failed = d.failCount ?? d.failureCount ?? d.failed ?? "-";
    const failedItems = d.failedItems ?? d.errors ?? d.validationErrors ?? [];

    return (
      <div className="flex flex-col gap-4">
        <div
          className="flex items-center gap-3 p-4 rounded-xl"
          style={{ backgroundColor: `${currentTheme.color}10` }}
        >
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center"
            style={{ backgroundColor: currentTheme.color }}
          >
            {isValidate ? (
              <CheckCircle className="w-6 h-6 text-white" />
            ) : (
              <Truck className="w-6 h-6 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="font-bold text-sm"
              style={{ color: currentTheme.color }}
            >
              {isValidate ? "Validation Complete" : "Upload Complete"}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {selectedFile?.name}
            </p>
            {d.processedAt && (
              <p className="text-[10px] text-gray-400 mt-0.5">
                Processed at: {d.processedAt}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Total Processed", value: total, color: "text-gray-700" },
            { label: "Success", value: success, color: "text-green-600" },
            {
              label: "Failed",
              value: failed,
              color: Number(failed) > 0 ? "text-red-500" : "text-gray-400",
            },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
              <p className={`text-2xl font-black ${color}`}>{value}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">
                {label}
              </p>
            </div>
          ))}
        </div>

        {failedItems.length > 0 && (
          <div className="border border-red-100 rounded-xl overflow-hidden">
            <div className="bg-red-50 px-4 py-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-xs font-semibold text-red-600">
                Failed Items ({failedItems.length})
              </span>
            </div>
            <div className="max-h-40 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-red-50/60 border-b border-red-100">
                    <th className="px-3 py-1.5 text-left font-semibold text-red-500">
                      Row
                    </th>
                    <th className="px-3 py-1.5 text-left font-semibold text-red-500">
                      Order ID
                    </th>
                    <th className="px-3 py-1.5 text-left font-semibold text-red-500">
                      Status
                    </th>
                    <th className="px-3 py-1.5 text-left font-semibold text-red-500">
                      Error
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-50">
                  {failedItems.map((item, i) => (
                    <tr key={i} className="hover:bg-red-50/40">
                      <td className="px-3 py-1.5 text-gray-500 font-mono">
                        {item.row ?? "-"}
                      </td>
                      <td className="px-3 py-1.5 text-gray-700 font-semibold">
                        {item.orderId ?? "-"}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">
                          {item.status ?? "-"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-500">
                        {item.error ?? (typeof item === "string" ? item : "-")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isValidate && (
          <button
            onClick={handleUpload}
            disabled={isLoading || Number(failed) > 0}
            className="w-full py-3 text-white font-bold rounded-xl text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:opacity-90"
            style={{ backgroundColor: currentTheme.color }}
          >
            {isLoading ? (
              <Loader className="w-4 h-4 animate-spin mx-auto" />
            ) : Number(failed) > 0 ? (
              "Fix Errors Before Uploading"
            ) : (
              "Proceed & Upload"
            )}
          </button>
        )}

        <button
          onClick={() => {
            setResult(null);
            setSelectedFile(null);
            setActiveStep("upload");
          }}
          className="w-full py-2.5 text-sm font-semibold rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Start Over
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{ backgroundColor: currentTheme.color }}
        >
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-white" />
            <h3 className="font-bold text-white text-base">
              Bulk Status Update via Excel
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5">
          {activeStep !== "result" ? (
            <div className="flex flex-col gap-4">
              <button
                onClick={handleDownloadTemplate}
                disabled={isLoading}
                className="flex items-center justify-between w-full p-4 rounded-xl border-2 border-dashed transition-all hover:bg-gray-50 group disabled:opacity-50"
                style={{ borderColor: `${currentTheme.color}40` }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${currentTheme.color}15` }}
                  >
                    <FileText
                      className="w-5 h-5"
                      style={{ color: currentTheme.color }}
                    />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-800">
                      Download Template
                    </p>
                    <p className="text-xs text-gray-400">
                      Get the Excel template with correct format
                    </p>
                  </div>
                </div>
                {isLoading ? (
                  <Loader className="w-4 h-4 animate-spin text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400 -rotate-90 group-hover:translate-x-0.5 transition-transform" />
                )}
              </button>

              <div
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onClick={() => fileInputRef.current?.click()}
                className="relative flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed cursor-pointer transition-all"
                style={{
                  borderColor: isDragging
                    ? currentTheme.color
                    : selectedFile
                      ? `${currentTheme.color}60`
                      : "#e5e7eb",
                  backgroundColor: isDragging
                    ? `${currentTheme.color}08`
                    : selectedFile
                      ? `${currentTheme.color}05`
                      : "transparent",
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
                {selectedFile ? (
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${currentTheme.color}15` }}
                    >
                      <FileText
                        className="w-6 h-6"
                        style={{ color: currentTheme.color }}
                      />
                    </div>
                    <p className="text-sm font-semibold text-gray-800 text-center max-w-[200px] truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                      className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 mt-1"
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                      <Plus className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-600">
                      Drop your Excel file here
                    </p>
                    <p className="text-xs text-gray-400">
                      or click to browse · .xlsx, .xls
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleValidate}
                  disabled={!selectedFile || isLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  style={{
                    borderColor: currentTheme.color,
                    color: currentTheme.color,
                  }}
                >
                  {isLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Validate (Dry Run)
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || isLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  {isLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Truck className="w-4 h-4" />
                  )}
                  Upload & Update
                </button>
              </div>

              <p className="text-[11px] text-gray-400 text-center">
                Tip: Use <strong>Validate</strong> first to check for errors
                before applying changes
              </p>
            </div>
          ) : (
            renderResult()
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Shipping Success Animation Popup ─────────────────────────────────────────
const ShippingSuccessPopup = ({
  isOpen,
  onClose,
  orderCount,
  currentTheme,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-[1.5rem] shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-300">
        <div
          className="px-5 py-3 flex items-center justify-between border-b"
          style={{
            backgroundColor: `${currentTheme.color}15`,
            borderColor: `${currentTheme.color}30`,
          }}
        >
          <h3
            className="font-extrabold text-base flex items-center gap-2"
            style={{ color: currentTheme.color }}
          >
            Order Shipped Successfully!
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full transition-colors hover:bg-black/5"
            style={{ color: currentTheme.color }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 pb-8 flex flex-col items-center">
          <div className="w-full h-32 relative flex items-center justify-center overflow-hidden">
            <div className="absolute bottom-8 left-0 right-0 h-[1.5px] bg-gray-100" />
            <div className="absolute bottom-8 left-0 animate-truck-run">
              <svg
                width="80"
                height="50"
                viewBox="0 0 100 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="5"
                  y="15"
                  width="60"
                  height="35"
                  rx="4"
                  fill={currentTheme.color}
                />
                <rect
                  x="15"
                  y="10"
                  width="18"
                  height="8"
                  rx="2"
                  fill="#FBBF24"
                />
                <path d="M65 25 H85 Q92 25 92 32 V50 H65 Z" fill="#F87171" />
                <path
                  d="M72 30 H82 Q85 30 85 33 V40 H72 Z"
                  fill="#E2E8F0"
                  fillOpacity="0.5"
                />
                <circle cx="22" cy="50" r="7" fill="#1E293B" />
                <circle cx="22" cy="50" r="3" fill="#94A3B8" />
                <circle cx="78" cy="50" r="7" fill="#1E293B" />
                <circle cx="78" cy="50" r="3" fill="#94A3B8" />
                <line
                  x1="0"
                  y1="30"
                  x2="4"
                  y2="30"
                  stroke={currentTheme.color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="animate-pulse"
                />
                <line
                  x1="-2"
                  y1="40"
                  x2="2"
                  y2="40"
                  stroke={currentTheme.color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="animate-pulse"
                />
              </svg>
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-success-appear opacity-0 pointer-events-none">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                style={{
                  backgroundColor: currentTheme.color,
                  boxShadow: `0 10px 25px -5px ${currentTheme.color}50`,
                }}
              >
                <Check className="w-8 h-8 text-white" strokeWidth={4} />
              </div>
            </div>
          </div>

          <div className="mt-2 text-center animate-content-fade opacity-0 fill-mode-forwards">
            <p
              className="font-bold tracking-widest text-[10px] uppercase mb-1"
              style={{ color: currentTheme.color }}
            >
              Confirmation
            </p>
            <h4 className="text-2xl font-black text-gray-800 leading-tight">
              {orderCount} {orderCount === 1 ? "ORDER" : "ORDERS"}
              <br />
              <span style={{ color: currentTheme.color }}>SHIPPED!</span>
            </h4>
          </div>

          <button
            onClick={onClose}
            className="mt-6 w-full py-3.5 text-white font-black rounded-xl shadow-xl transition-all hover:translate-y-[-2px] active:translate-y-[1px] animate-content-fade opacity-0 text-sm"
            style={{
              backgroundColor: currentTheme.color,
              boxShadow: `0 10px 20px -5px ${currentTheme.color}40`,
              animationDelay: "2.5s",
            }}
          >
            DONE
          </button>
        </div>
      </div>

      <style>{`
                @keyframes truckRun {
                    0% { transform: translateX(-100px); }
                    10% { transform: translateX(-100px); }
                    50% { transform: translateX(140px) rotate(-1deg); }
                    55% { transform: translateX(150px) rotate(1deg); }
                    90% { transform: translateX(450px); opacity: 1; }
                    100% { transform: translateX(500px); opacity: 0; }
                }
                @keyframes successAppear {
                    0% { transform: translate(-50%, -50%) scale(0) rotate(-45deg); opacity: 0; }
                    50% { transform: translate(-50%, -50%) scale(1.2) rotate(10deg); opacity: 1; }
                    100% { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1; }
                }
                @keyframes contentFade {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-truck-run { animation: truckRun 5s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
                .animate-success-appear { animation: successAppear 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 2s forwards; }
                .animate-content-fade { animation: contentFade 0.6s ease-out 2.8s forwards; }
            `}</style>
    </div>
  );
};

// ─── Popup Component ──────────────────────────────────────────────────────────
const Popup = ({ popup, onClose }) => {
  if (!popup) return null;
  const isSuccess = popup.type === "success";
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div
          className={`h-1.5 w-full ${isSuccess ? "bg-green-500" : "bg-red-500"}`}
        />
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div
              className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${isSuccess ? "bg-green-100" : "bg-red-100"}`}
            >
              {isSuccess ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-500" />
              )}
            </div>
            <div className="flex-1 pt-0.5">
              <h3
                className={`text-base font-bold ${isSuccess ? "text-green-700" : "text-red-700"}`}
              >
                {popup.title || (isSuccess ? "Success" : "Error")}
              </h3>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                {popup.message}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className={`px-5 py-2 rounded-lg text-sm font-semibold text-white ${isSuccess ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"}`}
            >
              {isSuccess ? "Great!" : "OK, Got it"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function Dispatched() {
  const { currentTheme } = useTheme();
  const { has, isAdmin } = usePermissions();
  const [activeTab, setActiveTab] = useState("Dispatched");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);

  const emptyFilters = {
    date: "",
    customStartDate: "",
    customEndDate: "",
    category: "",
    paymentMode: "",
    campaign: "",
    orderBy: "",
    actionBy: "",
  };
  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [tempFilters, setTempFilters] = useState(emptyFilters);

  // ✅ Backend se load hone waale filter dropdown options
  const [filterOptions, setFilterOptions] = useState({
    agents: [],
    teamLeads: [],
    categories: [],
    paymentModes: [],
    campaigns: [],
  });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);

  const [isCourierDropdownOpen, setIsCourierDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [selectedDateFilter, setSelectedDateFilter] = useState("All Time");
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isItemsPerPageOpen, setIsItemsPerPageOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourier, setSelectedCourier] = useState("All courier");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [popup, setPopup] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isShippingSuccessOpen, setIsShippingSuccessOpen] = useState(false);
  const [lastShippedCount, setLastShippedCount] = useState(0);

  const couriers = [
    "All courier",
    "DNST",
    "Ekart",
    "F-ship",
    "Shadowfax",
    "Speed post",
  ];
  const dateOptions = [
    "All Time",
    "Today",
    "Yesterday",
    "Last Week",
    "Last Month",
  ];
  const topDateKeyMap = {
    Today: "TODAY",
    Yesterday: "YESTERDAY",
    "Last Week": "LAST_WEEK",
    "Last Month": "LAST_MONTH",
  };

  const showPopup = (type, title, message) =>
    setPopup({ type, title, message });
  const closePopup = () => setPopup(null);

  // ✅ Filter dropdown options — API se load karo + fallback
  const fetchFilterOptions = async () => {
    setFilterOptionsLoading(true);
    try {
      let data = await ordersApi.getFilterOptions().catch(() => null);
      let payload = data?.data || data || {};

      let agents = Array.isArray(payload?.agents) ? payload.agents : [];
      let teamLeads = Array.isArray(payload?.teamLeads) ? payload.teamLeads : [];
      let categories = Array.isArray(payload?.categories) ? payload.categories : [];
      let paymentModes = Array.isArray(payload?.paymentModes) ? payload.paymentModes : [];
      let campaigns = Array.isArray(payload?.campaigns) ? payload.campaigns : [];

      if (!agents.length || !categories.length || !campaigns.length) {
        const [empRes, catRes, campRes] = await Promise.allSettled([
          employeesApi.getAllEmployees(),
          productsApi.getAllCategories(),
          orderCampaignApi.getAllCampaigns(),
        ]);

        if (!agents.length && empRes.status === "fulfilled") {
          const empRaw = empRes.value;
          const empArr = Array.isArray(empRaw) ? empRaw : (empRaw?.data || empRaw?.content || []);
          agents = empArr.map((e) => ({
            name: e.name || e.employeeName || e.fullName || "",
            id: e.id,
          })).filter((e) => e.name);
        }

        if (!categories.length && catRes.status === "fulfilled") {
          const catRaw = catRes.value;
          const catArr = Array.isArray(catRaw) ? catRaw : (catRaw?.data || []);
          categories = [...new Set(catArr.map((c) => typeof c === "string" ? c : (c?.categoryName || c?.name)).filter(Boolean))].sort();
        }

        if (!campaigns.length && campRes.status === "fulfilled") {
          const campRaw = campRes.value;
          const campArr = Array.isArray(campRaw) ? campRaw : (campRaw?.data || campRaw?.content || []);
          campaigns = [...new Set(campArr.map((c) => typeof c === "string" ? c : (c?.campaignName || c?.name || c?.title)).filter(Boolean))].sort();
        }
      }

      if (!paymentModes.length) {
        paymentModes = ["COD", "ONLINE", "PART_PAYMENT", "PREPAID"];
      }

      setFilterOptions({
        agents,
        teamLeads,
        categories,
        paymentModes,
        campaigns,
      });
    } catch (err) {
      console.error("fetchFilterOptions error:", err);
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchOrders = React.useCallback(
    async (page = 1, size = itemsPerPage, overrideFilters = undefined) => {
      setLoading(true);
      try {
        const filters = overrideFilters !== undefined ? overrideFilters : activeFilters;
        const params = {};
        if (selectedCourier && selectedCourier !== "All courier")
          params.courier = selectedCourier;
        if (searchTerm && searchTerm.trim())
          params.searchTerm = searchTerm.trim();

        // ✅ Date handling (backend CURL specs ke according)
        if (filters.date === "custom") {
          if (filters.customStartDate && filters.customEndDate) {
            params.startDate = filters.customStartDate;
            params.endDate = filters.customEndDate;
          }
        } else if (filters.date) {
          params.dateRangePreset = filters.date;
        } else if (selectedDateFilter && selectedDateFilter !== "All Time") {
          params.dateRangePreset = topDateKeyMap[selectedDateFilter] || selectedDateFilter.toUpperCase();
        }

        // ✅ Filter modal ke baaki fields — backend ko bhejo (empty hone par param nahi bhejte)
        if (filters.category) params.category = filters.category;
        if (filters.paymentMode) params.paymentMode = filters.paymentMode;
        if (filters.campaign) params.campaign = filters.campaign;
        if (filters.orderBy) params.orderBy = filters.orderBy; // Long employee ID
        if (filters.actionBy) params.actionBy = filters.actionBy; // Long employee ID

        params.page = page - 1;
        params.size = size;

        let response;
        switch (activeTab) {
          case "Dispatched":
            response = await ordersApi.getDispatched(params);
            break;
          case "Ready to ship":
            response = await ordersApi.getReadyToShipOrders(params);
            break;
          case "Shipped":
            response = await ordersApi.getShipped(params);
            break;
          default:
            response = await ordersApi.getDispatched(params);
        }

        const ordersData =
          response?.data?.content ||
          response?.content ||
          response?.data?.orders ||
          response?.orders ||
          response?.data ||
          (Array.isArray(response) ? response : []);

        const total =
          response?.data?.totalElements ??
          response?.totalElements ??
          response?.data?.total ??
          response?.totalCount ??
          response?.total ??
          response?.count ??
          (Array.isArray(ordersData) ? ordersData.length : 0);

        setOrders(Array.isArray(ordersData) ? ordersData : []);
        setTotalCount(typeof total === "number" ? total : (Array.isArray(ordersData) ? ordersData.length : 0));
      } catch (error) {
        console.error("Error fetching orders:", error);
        showPopup(
          "error",
          "Failed to fetch orders",
          error.message || "Something went wrong",
        );
        setOrders([]);
      } finally {
        setLoading(false);
      }
    },
    [
      activeTab,
      selectedCourier,
      selectedDateFilter,
      itemsPerPage,
      searchTerm,
      activeFilters,
    ],
  );

  useEffect(() => {
    fetchOrders(currentPage);
    setSelectedOrders([]);
  }, [
    activeTab,
    selectedCourier,
    selectedDateFilter,
    currentPage,
    itemsPerPage,
  ]);

  // Mount par upar wala effect already fetchOrders(currentPage) call karta hai,
  // aur ye bhi chalta tha → ek hi API 2 baar. Pehla render skip karo.
  const skipFirstSearch = useRef(true);
  useEffect(() => {
    if (skipFirstSearch.current) {
      skipFirstSearch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchOrders(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ── DISPATCHED → READY_TO_SHIP ────────────────────────────────────────────
  const handleReadyToShip = async () => {
    if (selectedOrders.length === 0) {
      showPopup(
        "error",
        "No orders selected",
        "Please select at least one order",
      );
      return;
    }
    setActionLoading(true);
    try {
      await bulkUpdateStatus(
        selectedOrders,
        "READY_TO_SHIP",
        "Bulk marked Ready to Ship",
      );

      try {
        message.loading({ content: "Generating labels...", key: "label" });
        const blob = await ordersApi.generateBulkLabels(selectedOrders);
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(
          "",
          "Print Labels",
          "width=900,height=700,scrollbars=yes,resizable=yes",
        );
        if (printWindow && !printWindow.closed) {
          printWindow.document.write(
            `<!DOCTYPE html><html><head><title>Print Labels</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;background:#f5f5f5;}.header{background:#fff;padding:15px 20px;border-bottom:1px solid #ddd;display:flex;justify-content:space-between;align-items:center;}.btn{padding:10px 20px;margin:0 5px;border:none;border-radius:5px;cursor:pointer;font-size:14px;font-weight:600;}.btn-print{background:#4CAF50;color:white;}.btn-download{background:#2196F3;color:white;}.btn-close{background:#f44336;color:white;}iframe{width:100%;height:calc(100vh - 60px);border:none;}</style></head><body><div class="header"><h3>Labels for ${selectedOrders.length} Orders</h3><div><button class="btn btn-print" onclick="window.print()">🖨️ Print</button><button class="btn btn-download" onclick="downloadPDF()">⬇️ Download</button><button class="btn btn-close" onclick="window.close()">✖️ Close</button></div></div><iframe src="${blobUrl}"></iframe><script>function downloadPDF(){const link=document.createElement('a');link.href='${blobUrl}';link.download='labels_${Date.now()}.pdf';link.click();}</script></body></html>`,
          );
          printWindow.document.close();
          message.success({
            content: `Labels ready! (${selectedOrders.length} orders)`,
            key: "label",
            duration: 3,
          });
        } else {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `labels_${selectedOrders.length}_orders_${Date.now()}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          message.info({
            content: "📥 Labels downloaded!",
            key: "label",
            duration: 5,
          });
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
      } catch (labelError) {
        console.error("Label generation error:", labelError);
        message.error({ content: "Failed to generate labels", key: "label" });
      }

      showPopup(
        "success",
        "Success!",
        `${selectedOrders.length} order(s) marked as Ready to Ship`,
      );
      setSelectedOrders([]);
      fetchOrders(currentPage);
    } catch (error) {
      showPopup(
        "error",
        "Action failed",
        error.message || "Failed to mark orders as Ready to Ship",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // ── READY_TO_SHIP → SHIPPED ───────────────────────────────────────────────
  const handleShipped = async () => {
    if (selectedOrders.length === 0) {
      showPopup(
        "error",
        "No orders selected",
        "Please select at least one order",
      );
      return;
    }
    setActionLoading(true);
    try {
      await bulkUpdateStatus(selectedOrders, "SHIPPED", "Bulk marked Shipped");
      setLastShippedCount(selectedOrders.length);
      setIsShippingSuccessOpen(true);
      setSelectedOrders([]);
      fetchOrders(currentPage);
    } catch (error) {
      showPopup(
        "error",
        "Action failed",
        error.message || "Failed to mark orders as Shipped",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // ── Shipped tab — generic bulk status updater ────────────────────────────
  const handleBulkStatusUpdate = async (status) => {
    if (selectedOrders.length === 0) return;
    setActionLoading(true);
    const statusLabels = {
      IN_TRANSIT: "In Transit",
      OUT_FOR_DELIVERY: "Out for Delivery",
      NDR: "NDR",
      RTO: "RTO",
      DELIVERED: "Delivered",
    };
    try {
      await bulkUpdateStatus(
        selectedOrders,
        status,
        `Bulk marked ${statusLabels[status] || status}`,
      );
      showPopup(
        "success",
        "Updated!",
        `${selectedOrders.length} order(s) marked as ${statusLabels[status] || status}`,
      );
      setSelectedOrders([]);
      fetchOrders(currentPage);
    } catch (error) {
      showPopup(
        "error",
        "Action failed",
        error.message || `Failed to update status to ${status}`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadLabels = async (specificOrderId = null) => {
    const orderIds = specificOrderId ? [specificOrderId] : selectedOrders;
    if (orderIds.length === 0) {
      showPopup(
        "error",
        "No orders selected",
        "Please select at least one order",
      );
      return;
    }
    setActionLoading(true);
    try {
      message.loading({ content: "Generating labels...", key: "label" });
      const blob = await ordersApi.generateBulkDeliveredLabels(orderIds);
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(
        "",
        "Print Labels",
        "width=900,height=700,scrollbars=yes,resizable=yes",
      );
      if (printWindow && !printWindow.closed) {
        printWindow.document.write(
          `<!DOCTYPE html><html><head><title>Print Labels</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;background:#f5f5f5;}.header{background:#fff;padding:15px 20px;border-bottom:1px solid #ddd;display:flex;justify-content:space-between;align-items:center;}.btn{padding:10px 20px;margin:0 5px;border:none;border-radius:5px;cursor:pointer;font-size:14px;font-weight:600;}.btn-print{background:#4CAF50;color:white;}.btn-download{background:#2196F3;color:white;}.btn-close{background:#f44336;color:white;}iframe{width:100%;height:calc(100vh - 60px);border:none;}</style></head><body><div class="header"><h3>Labels for ${orderIds.length} Orders</h3><div><button class="btn btn-print" onclick="window.print()">🖨️ Print</button><button class="btn btn-download" onclick="downloadPDF()">⬇️ Download</button><button class="btn btn-close" onclick="window.close()">✖️ Close</button></div></div><iframe src="${blobUrl}"></iframe><script>function downloadPDF(){const link=document.createElement('a');link.href='${blobUrl}';link.download='labels_${Date.now()}.pdf';link.click();}</script></body></html>`,
        );
        printWindow.document.close();
        message.success({
          content: `Labels ready! (${orderIds.length} orders)`,
          key: "label",
          duration: 3,
        });
      } else {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `labels_${orderIds.length}_orders_${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.info({
          content: "📥 Labels downloaded!",
          key: "label",
          duration: 5,
        });
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
      if (!specificOrderId) setSelectedOrders([]);
    } catch (error) {
      showPopup(
        "error",
        "Action failed",
        error.error || error.message || "Failed to generate labels",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ Ab client-side filtering ki zaroorat nahi — backend hi sab filter kar chuka hai
  const filteredOrders = orders;

  const isDark = currentTheme.textColor === "#FFFFFF";

  if (loading && orders.length === 0) {
    return (
      <div
        className={`p-4 min-h-screen flex items-center justify-center ${isDark ? "bg-[#0f172a]" : "bg-gray-50/50"}`}
      >
        <Loader
          className="w-8 h-8 animate-spin"
          style={{ color: currentTheme.color }}
        />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? "bg-[#0f172a]" : "bg-gray-50/30"}`}
    >
      <div className="flex flex-col gap-6">
        <Popup popup={popup} onClose={closePopup} />

        <ShippingSuccessPopup
          isOpen={isShippingSuccessOpen}
          onClose={() => setIsShippingSuccessOpen(false)}
          orderCount={lastShippedCount}
          currentTheme={currentTheme}
        />

        <ExcelBulkModal
          isOpen={isExcelModalOpen}
          onClose={() => setIsExcelModalOpen(false)}
          currentTheme={currentTheme}
          onSuccess={() => {
            fetchOrders(currentPage);
            showPopup(
              "success",
              "Bulk Update Complete!",
              "Orders have been updated from Excel file",
            );
          }}
        />

        {/* ── Header ── */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            {activeTab} Orders{" "}
            <span className="text-gray-400 text-lg font-normal">
              ({totalCount})
            </span>
          </h1>
          <div className="flex space-x-3 items-center">
            {has("DISPATCH_EDIT") && (
              <button
                onClick={() => setIsExcelModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-700"
              >
                <FileText className="w-4 h-4 text-green-600" />
                Bulk Excel Update
              </button>
            )}
            {has("DISPATCH_VIEW") && (
              <button
                onClick={() => {
                  setTempFilters(activeFilters);
                  setIsFilterModalOpen(true);
                }}
                className="relative p-2 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
              >
                <Filter className="w-5 h-5 text-gray-500" />
                {Object.values(activeFilters).some((v) => v !== "") && (
                  <span
                    className="absolute top-1 right-1 w-2 h-2 rounded-full"
                    style={{ backgroundColor: currentTheme.color }}
                  />
                )}
              </button>
            )}
            {has("DISPATCH_VIEW") && (
              <ExcelExportButton
                onClick={() => {
                  if (orders.length === 0) {
                    message.warning("No orders to export");
                    return;
                  }
                  setIsDownloading(true);
                  try {
                    const headers = [
                      "Order ID",
                      "Customer",
                      "Phone",
                      "Date",
                      "Category",
                      "Amount",
                      "Pay Mode",
                      "Campaign",
                      "Order By",
                    ];
                    const csvRows = orders.map((o) =>
                      [
                        `"${o.orderId || o.id || ""}"`,
                        `"${o.customerName || o.name || ""}"`,
                        `"${o.contactNumber || o.phone || ""}"`,
                        `"${o.createdAt ? new Date(o.createdAt.replace(" ", "T")).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }) : o.createdAt || "N/A"}"`,
                        `"${o.category || ""}"`,
                        (o.grandTotal || o.amount || 0) -
                          (o.prepaidAmount || 0),
                        `"${o.paymentMode || "COD"}"`,
                        `"${o.campaign || ""}"`,
                        `"${o.orderBy?.name || o.createdByName || ""}"`,
                      ].join(","),
                    );
                    const blob = new Blob(
                      [
                        "\uFEFF" +
                          headers.join(",") +
                          "\n" +
                          csvRows.join("\n"),
                      ],
                      { type: "text/csv;charset=utf-8;" },
                    );
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.setAttribute("download", `${activeTab}_Orders.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    message.success("Export successful");
                  } catch {
                    message.error("Export failed");
                  } finally {
                    setIsDownloading(false);
                  }
                }}
                isDownloading={isDownloading}
                label="DOWNLOAD EXCEL"
              />
            )}
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex items-center space-x-4 mb-6">
          <ExpandableSearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search by order id, name or phone"
          />

          <div className="relative">
            <button
              onClick={() => setIsItemsPerPageOpen(!isItemsPerPageOpen)}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
            >
              <span>
                {itemsPerPage === 99999 ? "All" : itemsPerPage} Orders
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            {isItemsPerPageOpen && (
              <div className="absolute top-full text-center left-0 mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-20">
                {[10, 20, 50, 99999].map((num) => (
                  <button
                    key={num}
                    onClick={() => {
                      setItemsPerPage(num);
                      setIsItemsPerPageOpen(false);
                      setCurrentPage(1);
                      fetchOrders(1, num);
                    }}
                    className={`w-full text-center px-4 py-2 text-sm hover:bg-gray-50 ${itemsPerPage === num ? "font-medium" : "text-gray-700"}`}
                    style={
                      itemsPerPage === num
                        ? {
                            color: currentTheme.color,
                            backgroundColor: `${currentTheme.color}10`,
                          }
                        : {}
                    }
                  >
                    {num === 99999 ? "All" : num}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{ backgroundColor: currentTheme.color }}
              onClick={() => setIsCourierDropdownOpen(!isCourierDropdownOpen)}
            >
              <Truck className="w-4 h-4" />
              <span>{selectedCourier}</span>
              <ChevronDown
                className={`w-4 h-4 ml-2 transition-transform ${isCourierDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isCourierDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-20">
                {couriers.map((courier) => (
                  <button
                    key={courier}
                    onClick={() => {
                      setSelectedCourier(courier);
                      setIsCourierDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm border-b border-gray-50 flex justify-between items-center ${selectedCourier === courier ? "font-medium" : "text-gray-700 hover:bg-gray-50"}`}
                    style={
                      selectedCourier === courier
                        ? {
                            color: currentTheme.color,
                            backgroundColor: `${currentTheme.color}10`,
                          }
                        : {}
                    }
                  >
                    <span>{courier}</span>
                    {selectedCourier === courier && (
                      <CheckCircle
                        className="w-4 h-4"
                        style={{ color: currentTheme.color }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
            >
              <Calendar className="w-4 h-4 text-orange-500" />
              <span>{selectedDateFilter}</span>
              <ChevronDown
                className={`w-4 h-4 ml-2 transition-transform ${isDateDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isDateDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-20">
                {dateOptions.map((date) => (
                  <button
                    key={date}
                    onClick={() => {
                      setSelectedDateFilter(date);
                      setIsDateDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm border-b border-gray-50 flex justify-between items-center ${selectedDateFilter === date ? "font-medium" : "text-gray-700 hover:bg-gray-50"}`}
                    style={
                      selectedDateFilter === date
                        ? {
                            color: currentTheme.color,
                            backgroundColor: `${currentTheme.color}10`,
                          }
                        : {}
                    }
                  >
                    <span>{date}</span>
                    {selectedDateFilter === date && (
                      <CheckCircle
                        className="w-4 h-4"
                        style={{ color: currentTheme.color }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-8 border-b border-gray-200 mb-6">
          {["Dispatched", "Ready to ship", "Shipped"].map((tab) => (
            <button
              key={tab}
              className={`pb-4 px-2 font-medium text-sm transition-colors relative ${activeTab === tab ? "" : "text-gray-500 hover:text-gray-700"}`}
              style={{
                color: activeTab === tab ? currentTheme.color : undefined,
              }}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
            >
              {tab}
              {activeTab === tab && (
                <div
                  className="absolute bottom-0 left-0 w-full h-0.5 rounded-t-full"
                  style={{ backgroundColor: currentTheme.color }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Table */}
        {filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl shadow-sm border border-gray-200">
            <Box className="w-16 h-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-400">
              No orders found
            </h2>
            <p className="text-sm text-gray-400 mt-2">
              Try adjusting your filters or search term
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px]">
                <thead>
                  <tr
                    className="text-white text-xs uppercase tracking-wider"
                    style={{ backgroundColor: currentTheme.color }}
                  >
                    {has("DISPATCH_EDIT") && (
                      <th className="p-4 w-20">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => {
                              const currentIds = filteredOrders.map(
                                (o) => o.id,
                              );
                              const allSelected = currentIds.every((id) =>
                                selectedOrders.includes(id),
                              );
                              if (allSelected)
                                setSelectedOrders((prev) =>
                                  prev.filter((id) => !currentIds.includes(id)),
                                );
                              else
                                setSelectedOrders((prev) => [
                                  ...new Set([...prev, ...currentIds]),
                                ]);
                            }}
                            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors"
                          >
                            <Plus
                              className={`w-5 h-5 text-white transition-transform ${filteredOrders.length > 0 && filteredOrders.every((o) => selectedOrders.includes(o.id)) ? "rotate-45" : ""}`}
                            />
                          </button>
                          <span className="ml-2 text-[10px] font-bold">
                            ALL
                          </span>
                        </div>
                      </th>
                    )}
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      ORDER ID
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      DATE
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      NAME
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      PRODUCT
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      CATEGORY
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      REST AMT.
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      PAY MODE
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      ORDER CAMPAIGN
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      ORDER BY
                    </th>
                    <th className="px-6 py-4 text-left font-medium whitespace-nowrap">
                      ACTION BY
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOrders.map((order) => {
                    const isSelected = selectedOrders.includes(order.id);
                    return (
                      <tr
                        key={order.id}
                        className="hover:bg-gray-50 transition-colors group cursor-pointer"
                        style={
                          isSelected
                            ? { backgroundColor: `${currentTheme.color}10` }
                            : {}
                        }
                        onClick={() => {
                          if (has("DISPATCH_EDIT"))
                            setSelectedOrders((prev) =>
                              prev.includes(order.id)
                                ? prev.filter((id) => id !== order.id)
                                : [...prev, order.id],
                            );
                        }}
                      >
                        {has("DISPATCH_EDIT") && (
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-center">
                              <button
                                className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${isSelected ? "text-white" : "border-emerald-200 text-emerald-500 hover:bg-emerald-50"}`}
                                style={
                                  isSelected
                                    ? {
                                        backgroundColor: currentTheme.color,
                                        borderColor: currentTheme.color,
                                      }
                                    : {
                                        borderColor: `${currentTheme.color}40`,
                                        color: currentTheme.color,
                                      }
                                }
                              >
                                {isSelected ? (
                                  <Check className="w-5 h-5" strokeWidth={3} />
                                ) : (
                                  <Plus className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-2">
                          <span className="text-sm font-semibold text-gray-900">
                            {order.orderId || order.id}
                          </span>
                        </td>
                        <td className="px-6 py-2">
                          <span className="text-sm text-gray-600 whitespace-nowrap">
                            {order.createdAt
                              ? new Date(
                                  order.createdAt.replace(" ", "T"),
                                ).toLocaleString("en-IN", {
                                  timeZone: "Asia/Kolkata",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                  hour12: true,
                                })
                              : order.date || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-2">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">
                              {order.customerName || order.name || "N/A"}
                            </span>
                            <div className="flex items-center mt-1 space-x-2">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {order.contactNumber || order.phone || "N/A"}
                              </span>
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center"
                                style={{
                                  backgroundColor: `${currentTheme.color}15`,
                                }}
                              >
                                <MessageCircle
                                  className="w-3 h-3"
                                  style={{ color: currentTheme.color }}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-2">
                          <div className="flex flex-col gap-1">
                            {order.orderItems?.length > 0 ? (
                              order.orderItems.map((item, i) => (
                                <span
                                  key={i}
                                  className="text-sm text-gray-700 max-w-[200px] block truncate"
                                  title={item.productName}
                                >
                                  {item.productName}{" "}
                                  {order.orderItems.length > 1 && (
                                    <span className="text-xs text-gray-400">
                                      x{item.quantity}
                                    </span>
                                  )}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-gray-500">N/A</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-2">
                          <span className="text-sm text-gray-500">
                            {order.category || "Ayurvedic"}
                          </span>
                        </td>
                        <td className="px-6 py-2">
                          <span
                            className="text-sm font-semibold"
                            style={{ color: currentTheme.color }}
                          >
                            ₹
                            {(
                              (order.grandTotal || order.amount || 0) -
                              (order.prepaidAmount || 0)
                            ).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-2">
                          <span className="text-sm text-gray-700 font-medium">
                            {order.paymentMode === "ONLINE"
                              ? "Online"
                              : order.paymentMode === "CASH" ||
                                  order.paymentMode === "COD"
                                ? "COD"
                                : order.paymentMode || "COD"}
                          </span>
                        </td>
                        <td className="px-6 py-2">
                          <span className="text-sm text-gray-700">
                            {order.campaign || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-2">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">
                              {order.orderBy?.name ||
                                order.createdByName ||
                                "N/A"}
                            </span>
                            <span className="text-xs text-gray-500">
                              Emp:{" "}
                              {order.orderBy?.empId ||
                                order.createdById ||
                                "N/A"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-2">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">
                              {order.actionUser?.name ||
                                order.assignedToName ||
                                "N/A"}
                            </span>
                            <span className="text-xs text-gray-500">
                              Emp:{" "}
                              {order.actionUser?.empId ||
                                order.assignedToId ||
                                "N/A"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => {
                setCurrentPage(page);
                fetchOrders(page, itemsPerPage);
              }}
            />
          </div>
        )}

        {/* Floating Action Buttons */}
        {activeTab === "Dispatched" &&
          selectedOrders.length > 0 &&
          has("DISPATCH_EDIT") && (
            <div className="fixed bottom-6 right-16">
              <button
                onClick={handleReadyToShip}
                disabled={actionLoading}
                className="flex items-center space-x-2 px-6 py-3 bg-orange-400 hover:bg-orange-500 text-white font-medium rounded-lg shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <CheckCircle className="w-5 h-5" />
                )}
                <span>Ready to ship ({selectedOrders.length})</span>
              </button>
            </div>
          )}

        {activeTab === "Ready to ship" &&
          selectedOrders.length > 0 &&
          has("DISPATCH_EDIT") && (
            <div className="fixed bottom-6 right-16 flex items-center space-x-3">
              <button
                onClick={() => handleDownloadLabels()}
                disabled={actionLoading}
                className="flex items-center space-x-2 px-2 py-3 bg-white font-medium rounded-lg shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-2 hover:bg-gray-50"
                style={{
                  color: currentTheme.color,
                  borderColor: currentTheme.color,
                }}
              >
                {actionLoading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <FileText className="w-5 h-5" />
                )}
                <span>Regenerate Label ({selectedOrders.length})</span>
              </button>
              <button
                onClick={handleShipped}
                disabled={actionLoading}
                className="flex items-center space-x-2 px-6 py-3 text-white font-medium rounded-lg shadow-lg transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.color }}
              >
                {actionLoading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <div className="bg-white rounded-full p-1 flex items-center justify-center">
                    <Truck
                      className="w-4 h-4"
                      style={{ color: currentTheme.color }}
                    />
                  </div>
                )}
                <span>Shipped ({selectedOrders.length})</span>
              </button>
            </div>
          )}

        {/* Shipped Tab — Bulk Status Actions */}
        {activeTab === "Shipped" &&
          selectedOrders.length > 0 &&
          has("DISPATCH_EDIT") && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white border border-gray-200 rounded-2xl shadow-2xl px-4 py-3 z-50">
              <span className="text-xs font-bold text-gray-400 mr-2 whitespace-nowrap">
                {selectedOrders.length} selected →
              </span>
              <button
                onClick={() => handleBulkStatusUpdate("IN_TRANSIT")}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 transition-colors shadow-sm"
              >
                <Truck className="w-3.5 h-3.5" /> In Transit
              </button>
              <button
                onClick={() => handleBulkStatusUpdate("OUT_FOR_DELIVERY")}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 transition-colors shadow-sm"
              >
                <Box className="w-3.5 h-3.5" /> OFD
              </button>
              <button
                onClick={() => handleBulkStatusUpdate("NDR")}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 transition-colors shadow-sm"
              >
                <AlertCircle className="w-3.5 h-3.5" /> NDR
              </button>
              <button
                onClick={() => handleBulkStatusUpdate("RTO")}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 transition-colors shadow-sm"
              >
                <X className="w-3.5 h-3.5" /> RTO
              </button>
              <button
                onClick={() => handleBulkStatusUpdate("DELIVERED")}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white bg-green-500 hover:bg-green-600 disabled:opacity-50 transition-colors shadow-sm"
              >
                <CheckCircle className="w-3.5 h-3.5" /> Delivered
              </button>
              {actionLoading && (
                <Loader className="w-4 h-4 animate-spin text-gray-400 ml-1" />
              )}
            </div>
          )}

        {/* Filter Modal */}
        {isFilterModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
              <div
                className="flex items-center justify-between px-6 py-4 border-b border-gray-200"
                style={{ background: currentTheme.color }}
              >
                <h2 className="text-lg font-bold text-white">Filter Orders</h2>
                <button
                  onClick={() => setIsFilterModalOpen(false)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              <div className="p-6 pb-48 overflow-visible flex-grow">
                <div className="grid grid-cols-2 gap-6 relative">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Range
                    </label>
                    <ThemeSelect
                      value={tempFilters.date}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, date: val })
                      }
                      options={[
                        { label: "All Time", value: "" },
                        { label: "Today", value: "TODAY" },
                        { label: "Yesterday", value: "YESTERDAY" },
                        { label: "This Week", value: "THIS_WEEK" },
                        { label: "Last Week", value: "LAST_WEEK" },
                        { label: "This Month", value: "THIS_MONTH" },
                        { label: "Last Month", value: "LAST_MONTH" },
                        { label: "Last 7 Days", value: "LAST_7_DAYS" },
                        { label: "Last 30 Days", value: "LAST_30_DAYS" },
                        { label: "Custom Range", value: "custom" },
                      ]}
                      icon={Calendar}
                      className="w-full"
                      placeholder="Select Date"
                    />
                    {tempFilters.date === "custom" && (
                      <div className="mt-2.5">
                        <RangePicker
                          value={
                            tempFilters.customStartDate && tempFilters.customEndDate
                              ? [dayjs(tempFilters.customStartDate), dayjs(tempFilters.customEndDate)]
                              : null
                          }
                          onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                              setTempFilters({
                                ...tempFilters,
                                customStartDate: dates[0].format("YYYY-MM-DD"),
                                customEndDate: dates[1].format("YYYY-MM-DD"),
                              });
                            } else {
                              setTempFilters({
                                ...tempFilters,
                                customStartDate: "",
                                customEndDate: "",
                              });
                            }
                          }}
                          format="DD-MM-YYYY"
                          popupClassName="!z-[100005]"
                          style={{
                            width: "100%",
                            borderRadius: "12px",
                            padding: "8px 14px",
                            borderColor: "#e2e8f0",
                          }}
                          size="middle"
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <ThemeSelect
                      value={tempFilters.category}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, category: val })
                      }
                      options={[
                        { label: "All Categories", value: "" },
                        ...filterOptions.categories.map((c) => ({
                          label: typeof c === "object" ? (c.categoryName || c.name || String(c)) : String(c),
                          value: typeof c === "object" ? (c.categoryName || c.name || c) : c,
                        })),
                      ]}
                      icon={Tag}
                      className="w-full"
                      placeholder="All Categories"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Mode
                    </label>
                    <ThemeSelect
                      value={tempFilters.paymentMode}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, paymentMode: val })
                      }
                      options={[
                        { label: "All Modes", value: "" },
                        ...filterOptions.paymentModes.map((p) => ({
                          label: typeof p === "object" ? (p.label || p.name || String(p)) : String(p),
                          value: typeof p === "object" ? (p.value || p.id || p) : p,
                        })),
                      ]}
                      icon={CreditCard}
                      className="w-full"
                      placeholder="All Modes"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Campaign
                    </label>
                    <ThemeSelect
                      value={tempFilters.campaign}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, campaign: val })
                      }
                      options={[
                        { label: "All Campaigns", value: "" },
                        ...filterOptions.campaigns.map((c) => ({
                          label: typeof c === "object" ? (c.campaignName || c.name || c.title || String(c.id)) : String(c),
                          value: typeof c === "object" ? (c.campaignName || c.name || c.id) : c,
                        })),
                      ]}
                      icon={Layout}
                      className="w-full"
                      placeholder="All Campaigns"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order By
                    </label>
                    <ThemeSelect
                      value={tempFilters.orderBy}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, orderBy: val })
                      }
                      options={[
                        { label: "All Users", value: "" },
                        ...filterOptions.agents.map((a) => ({
                          label: a.name || a.employeeName || a.fullName || String(a.id),
                          value: a.id || a.empId || a.name,
                        })),
                      ]}
                      icon={User}
                      className="w-full"
                      placeholder="All Users"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action By
                    </label>
                    <ThemeSelect
                      value={tempFilters.actionBy}
                      onChange={(val) =>
                        setTempFilters({ ...tempFilters, actionBy: val })
                      }
                      options={[
                        { label: "All Users", value: "" },
                        ...filterOptions.agents.map((a) => ({
                          label: a.name || a.employeeName || a.fullName || String(a.id),
                          value: a.id || a.empId || a.name,
                        })),
                      ]}
                      icon={User}
                      className="w-full"
                      placeholder="All Users"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3 bg-gray-50 flex-shrink-0">
                <button
                  onClick={() => {
                    setTempFilters(emptyFilters);
                    setActiveFilters(emptyFilters);
                    setCurrentPage(1);
                    setIsFilterModalOpen(false);
                    fetchOrders(1, itemsPerPage, emptyFilters);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={() => {
                    setActiveFilters(tempFilters);
                    setCurrentPage(1);
                    setIsFilterModalOpen(false);
                    fetchOrders(1, itemsPerPage, tempFilters);
                  }}
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-sm hover:shadow"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dispatched;
